<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ga4API from 'dashboard/api/ga4';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      configs: [],
      form: { name: '', measurement_id: '', api_secret: '' },
      saving: false,
    };
  },
  computed: {
    canSave() {
      return (
        this.form.name.trim() &&
        this.form.measurement_id.trim() &&
        this.form.api_secret.trim()
      );
    },
  },
  mounted() {
    this.fetchConfigs();
  },
  methods: {
    async fetchConfigs() {
      try {
        const { data } = await ga4API.getConfigs();
        this.configs = data || [];
      } catch (error) {
        this.configs = [];
      }
    },
    async saveConfig() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        await ga4API.createConfig({
          name: this.form.name.trim(),
          measurement_id: this.form.measurement_id.trim(),
          api_secret: this.form.api_secret.trim(),
          enabled: true,
        });
        this.form = { name: '', measurement_id: '', api_secret: '' };
        useAlert(this.$t('META_ADS.GA4.SAVED'));
        this.fetchConfigs();
      } catch (error) {
        useAlert(this.$t('META_ADS.GA4.SAVE_ERROR'));
      } finally {
        this.saving = false;
      }
    },
    async toggleConfig(config) {
      try {
        await ga4API.updateConfig(config.id, { enabled: !config.enabled });
        this.fetchConfigs();
      } catch (error) {
        useAlert(this.$t('META_ADS.GA4.SAVE_ERROR'));
      }
    },
    async removeConfig(id) {
      try {
        await ga4API.deleteConfig(id);
        this.fetchConfigs();
      } catch (error) {
        useAlert(this.$t('META_ADS.GA4.SAVE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.GA4.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.GA4.DESCRIPTION') }}
      </p>
    </div>

    <!-- Send events (Measurement Protocol) -->
    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="min-w-0">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('META_ADS.GA4.SEND.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('META_ADS.GA4.SEND.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.GA4.SEND.NAME') }}
          <input
            v-model="form.name"
            type="text"
            :placeholder="$t('META_ADS.GA4.SEND.NAME_PLACEHOLDER')"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.GA4.SEND.MEASUREMENT_ID') }}
          <input
            v-model="form.measurement_id"
            type="text"
            :placeholder="$t('META_ADS.GA4.SEND.MEASUREMENT_ID_PLACEHOLDER')"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.GA4.SEND.API_SECRET') }}
          <input
            v-model="form.api_secret"
            type="password"
            autocomplete="off"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </label>
      </div>
      <div class="flex justify-end">
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.GA4.SEND.ADD')"
          :is-loading="saving"
          :disabled="!canSave"
          sm
          @click="saveConfig"
        />
      </div>

      <div
        v-if="configs.length"
        class="flex flex-col divide-y rounded-lg divide-n-weak bg-n-alpha-black2"
      >
        <div
          v-for="config in configs"
          :key="config.id"
          class="flex items-center justify-between gap-3 px-4 py-3"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium truncate text-n-slate-12">
              {{ config.name }}
            </p>
            <p class="text-xs text-n-slate-10">{{ config.measurement_id }}</p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                config.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-n-slate-3 text-n-slate-10'
              "
            >
              {{
                config.enabled
                  ? $t('META_ADS.GA4.SEND.ENABLED')
                  : $t('META_ADS.GA4.SEND.DISABLED')
              }}
            </span>
            <NextButton
              :icon="config.enabled ? 'i-lucide-pause' : 'i-lucide-play'"
              slate
              faded
              xs
              @click="toggleConfig(config)"
            />
            <NextButton
              icon="i-lucide-trash-2"
              ruby
              faded
              xs
              @click="removeConfig(config.id)"
            />
          </div>
        </div>
      </div>

      <div
        class="flex items-start gap-2 p-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        <span class="i-lucide-workflow text-n-slate-10 mt-0.5 shrink-0" />
        <span>{{ $t('META_ADS.GA4.SEND.NOTE') }}</span>
      </div>
    </div>
  </div>
</template>
